import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import styled from 'foundations-theming/utils';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Small } from 'foundations-components/transitional/components/Small';
import { Table } from 'foundations-components/transitional/components/Table';
import { Link } from 'foundations-components/transitional/components/Link';
import ContentRoutes from 'ContentUtils/Routes';
import { CATEGORY_IDS } from 'ContentUtils/constants/Categories';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const HEADER_HEIGHT = 44;
const ROW_HEIGHT = 72;
const OVERFLOW_HEIGHT = ROW_HEIGHT * 0.4;
const VISIBLE_ROWS = 4;
const SCROLLABLE_CONTAINER_HEIGHT = HEADER_HEIGHT + ROW_HEIGHT * VISIBLE_ROWS + OVERFLOW_HEIGHT;
const ScrollableContainer = styled.div.withConfig({
  displayName: "CosUsageModal__ScrollableContainer",
  componentId: "sc-91a199-0"
})(["position:relative;"]);
const ScrollableContainerContents = styled.div.withConfig({
  displayName: "CosUsageModal__ScrollableContainerContents",
  componentId: "sc-91a199-1"
})(["max-height:", "px;overflow-y:auto;"], SCROLLABLE_CONTAINER_HEIGHT);
const MODAL_WIDTH = 540;
export default class CosUsageModal extends PureComponent {
  constructor(...args) {
    super(...args);
    this.renderSection = (type, contents) => {
      return /*#__PURE__*/_jsxs(Table, {
        "data-test-id": `${type}-usage`,
        children: [/*#__PURE__*/_jsx("thead", {
          children: /*#__PURE__*/_jsx("tr", {
            children: /*#__PURE__*/_jsx("th", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: this.getLangKey(type)
              })
            })
          })
        }), /*#__PURE__*/_jsx("tbody", {
          children: contents.map(({
            id,
            name,
            categoryId,
            subcategory
          }) => /*#__PURE__*/_jsx("tr", {
            children: /*#__PURE__*/_jsxs("td", {
              children: [/*#__PURE__*/_jsx("div", {
                children: /*#__PURE__*/_jsx(Link, {
                  "data-test-id": "cos-page-links",
                  use: "blue",
                  href: ContentRoutes.details(id, categoryId, subcategory),
                  children: name
                })
              }), /*#__PURE__*/_jsx("div", {
                children: /*#__PURE__*/_jsx(Small, {
                  use: "help",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: this.getLangKey('listItemAdditionalInfo'),
                    options: {
                      contentType: this.getCosType(categoryId)
                    }
                  })
                })
              })]
            })
          }, id))
        })]
      });
    };
  }
  getLangKey(suffix) {
    return `ContentComponents.cosUsageList.${suffix}`;
  }
  getCosType(categoryId) {
    switch (categoryId) {
      case CATEGORY_IDS.LANDING_PAGE:
        return I18n.text(this.getLangKey('cosTypes.landingPage'));
      case CATEGORY_IDS.SITE_PAGE:
        return I18n.text(this.getLangKey('cosTypes.sitePage'));
      case CATEGORY_IDS.BLOG_POST:
        return I18n.text(this.getLangKey('cosTypes.blogPost'));
      case CATEGORY_IDS.BLOG_LISTING_PAGE:
        return I18n.text(this.getLangKey('cosTypes.blogListingPage'));
      case CATEGORY_IDS.WEB_INTERACTIVE:
        return I18n.text(this.getLangKey('cosTypes.webInteractive'));
      case CATEGORY_IDS.EMAIL:
        return I18n.text(this.getLangKey('cosTypes.email'));
      default:
        console.warn(`Could not get COS type for unknown category '${categoryId}'.`);
        return I18n.text(this.getLangKey('cosTypes.unknown'));
    }
  }
  render() {
    const {
      contents,
      title,
      onClose,
      isLoading
    } = this.props;
    const content = [];
    const webInteractives = [];
    const emails = [];
    contents.forEach(p => {
      switch (p.categoryId) {
        case CATEGORY_IDS.WEB_INTERACTIVE:
          webInteractives.push(p);
          break;
        case CATEGORY_IDS.EMAIL:
          emails.push(p);
          break;
        default:
          content.push(p);
      }
    });
    return /*#__PURE__*/_jsxs(Modal, {
      "data-test-id": "cosUsageModal",
      width: MODAL_WIDTH,
      onEsc: onClose,
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(DialogHeader, {
        children: /*#__PURE__*/_jsx("h4", {
          children: title
        })
      }), /*#__PURE__*/_jsx(DialogBody, {
        children: isLoading ? /*#__PURE__*/_jsx("div", {
          style: {
            height: '200px'
          },
          children: /*#__PURE__*/_jsx(LoadingSpinner, {
            grow: true
          })
        }) : /*#__PURE__*/_jsx(ScrollableContainer, {
          children: /*#__PURE__*/_jsxs(ScrollableContainerContents, {
            children: [content.length > 0 && this.renderSection('contents', content), webInteractives.length > 0 && this.renderSection('interactives', webInteractives), emails.length > 0 && this.renderSection('emails', emails)]
          })
        })
      })]
    });
  }
}
CosUsageModal.propTypes = {
  contents: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    categoryId: PropTypes.number.isRequired,
    subcategory: PropTypes.string
  })).isRequired,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired
};